// Typed calendar days for the filters panel's date fields (#11426
// filters-v9 F3). A day is a YYYY-MM-DD string end to end — parsing and
// arithmetic stay in calendar space, never through a Date-object timezone
// (AGENTS.md).

const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const daysInMonth = (year: number, month: number): number =>
  month === 2 && ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0)
    ? 29
    : DAYS_IN_MONTH[month - 1];

const validDay = (year: number, month: number, day: number): boolean =>
  month >= 1 &&
  month <= 12 &&
  day >= 1 &&
  day <= daysInMonth(year, month) &&
  year >= 1000 &&
  year <= 9999;

const iso = (year: number, month: number, day: number): string =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const monthIndex = (word: string): number | null => {
  const w = word.toLowerCase().replace(/[.,]/g, "");
  if (w.length < 3) return null;
  const hit = MONTHS.findIndex((m) => m.startsWith(w));
  return hit === -1 ? null : hit + 1;
};

// Accepts ISO (2026-06-05), US slash (6/5/2026), and textual-month forms
// ("5 Jun 2026", "Jun 5, 2026"). Anything else — including partial input —
// is null, and the field keeps the raw text until it parses.
export const parseTypedDay = (input: string): string | null => {
  const t = input.trim();
  if (!t) return null;

  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return validDay(year, month, day) ? iso(year, month, day) : null;
  }

  m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const [month, day, year] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return validDay(year, month, day) ? iso(year, month, day) : null;
  }

  m = t.match(/^(\d{1,2})\s+([A-Za-z.]+),?\s+(\d{4})$/);
  if (m) {
    const month = monthIndex(m[2]);
    const [day, year] = [Number(m[1]), Number(m[3])];
    return month !== null && validDay(year, month, day)
      ? iso(year, month, day)
      : null;
  }

  m = t.match(/^([A-Za-z.]+)\s+(\d{1,2}),?\s+(\d{4})$/);
  if (m) {
    const month = monthIndex(m[1]);
    const [day, year] = [Number(m[2]), Number(m[3])];
    return month !== null && validDay(year, month, day)
      ? iso(year, month, day)
      : null;
  }

  return null;
};

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const dayDisplay = (day: string, year: (y: string) => string): string => {
  const m = day.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return day;
  return `${Number(m[3])} ${MONTH_ABBR[Number(m[2]) - 1]} ${year(m[1])}`;
};

export const formatDayDisplay = (day: string): string =>
  dayDisplay(day, (y) => y);

// The at-rest dress for the panel's narrow fields (#11426 Aug-31, CD
// ruling): "30 Jul 26" — the century goes, nothing else. The month stays a
// WORD, which is what keeps a shortened date unambiguous across locales
// (a numeric dd/mm vs mm/dd is the trap a compact format usually falls
// into); a field being edited shows the full form via formatDayDisplay.
export const formatDayDisplayCompact = (day: string): string =>
  dayDisplay(day, (y) => y.slice(2));

export type DatePreset = "last30" | "quarter" | "year";

const shiftDays = (day: string, delta: number): string => {
  const m = day.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return day;
  let [year, month, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  d += delta;
  while (d < 1) {
    month -= 1;
    if (month < 1) {
      month = 12;
      year -= 1;
    }
    d += daysInMonth(year, month);
  }
  while (d > daysInMonth(year, month)) {
    d -= daysInMonth(year, month);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return iso(year, month, d);
};

// The quiet preset links under the range row: each is a closed range ending
// on the reference day (the viewer's current local day).
export const presetRange = (
  preset: DatePreset,
  today: string
): [string, string] => {
  const m = today.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return [today, today];
  const [year, month] = [Number(m[1]), Number(m[2])];
  switch (preset) {
    case "last30":
      return [shiftDays(today, -30), today];
    case "quarter":
      return [iso(year, Math.floor((month - 1) / 3) * 3 + 1, 1), today];
    case "year":
      return [iso(year, 1, 1), today];
  }
};
